import { useEffect, useState } from "react";
import {
    Navigate,
    Route,
    BrowserRouter as Router,
    Routes,
} from "react-router-dom";
import Auth from "./components/Auth";
import { CDDRecordsDetails } from "./components/cdd-records";
import { ErrorBoundary } from "./components/ErrorBoundary";
import C2Invitations from "./components/portal/C2Invitations";
import CDDRecordDetail from "./components/portal/CDDRecordDetail";
import CDDRecords from "./components/portal/CDDRecords";
import Dashboard from "./components/portal/Dashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import { CognitoProvider } from "./contexts/CognitoProvider";
import { ConfigProvider } from "./contexts/ConfigContext";

import { useQueryClient } from "@tanstack/react-query";
import "./App.css";
import { GlobalConnectionToast } from "./components/common/GlobalConnectionToast";
import LoadingScreen from "./components/LoadingScreen";
import C2Profile from "./components/portal/C2Profile";
import C2RiskAssessment from "./components/portal/C2RiskAssessment";
import FAQ from "./components/portal/FAQ";
import Forms from "./components/portal/Forms";
import MyAccount from "./components/portal/MyAccount";
import SelfAssessment from "./components/portal/SelfAssessment";
import { QueryProvider } from "./contexts/QueryProvider";
import { WebSocketProvider } from "./contexts/WebSocketProvider";
import { dashboardKeys } from "./hooks/useDashboardData";
import { useRealtimeUpdates } from "./hooks/useRealtimeUpdates";
import { applyThemeColors, loadConfig } from "./services/configService";
import { BackendConfig, DEFAULT_BACKEND_CONFIG } from "./types/config";

function App() {
  const [config, setConfig] = useState<BackendConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const loadedConfig = await loadConfig();
        setConfig(loadedConfig);
        applyThemeColors(loadedConfig);
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "Failed to load configuration";
        setError(errorMsg);
        setConfig(DEFAULT_BACKEND_CONFIG);
        applyThemeColors(DEFAULT_BACKEND_CONFIG);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <ConfigProvider config={config} isLoading={isLoading} error={error}>
      <CognitoProvider>
        <WebSocketProvider>
          <QueryProvider>
            <ErrorBoundary>
              <Router>
                <AppLayout />
              </Router>
            </ErrorBoundary>
          </QueryProvider>
        </WebSocketProvider>
      </CognitoProvider>
    </ConfigProvider>
  );
}

function GlobalRealtimeSubscriber() {
  const queryClient = useQueryClient();
  useRealtimeUpdates({
    onCDDRecordUpdated: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.cddRecords });
    },
    onRecordStatusChanged: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.cddRecords });
    },
    onDashboardUpdated: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
    },
  });
  return null;
}

function AppLayout() {
  return (
    <>
      <GlobalRealtimeSubscriber />
      <GlobalConnectionToast />
      <Routes>
        <Route path="/login" element={<Auth />} />
        <Route path="/portal">
          <Route
            path="dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="records"
            element={
              <ProtectedRoute>
                <CDDRecords />
              </ProtectedRoute>
            }
          />
          <Route
            path="cdd-detail/:id"
            element={
              <ProtectedRoute>
                <CDDRecordDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="cdd-records/:id"
            element={
              <ProtectedRoute>
                <CDDRecordsDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="profile/:id"
            element={
              <ProtectedRoute>
                <C2Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="invitations"
            element={
              <ProtectedRoute>
                <C2Invitations />
              </ProtectedRoute>
            }
          />
          <Route
            path="account"
            element={
              <ProtectedRoute>
                <MyAccount />
              </ProtectedRoute>
            }
          />
          <Route
            path="faqs"
            element={
              <ProtectedRoute>
                <FAQ />
              </ProtectedRoute>
            }
          />
          <Route
            path="forms"
            element={
              <ProtectedRoute>
                <Forms />
              </ProtectedRoute>
            }
          />
          <Route
            path="self-assessment"
            element={
              <ProtectedRoute>
                <SelfAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path="risk-assessment"
            element={
              <ProtectedRoute>
                <C2RiskAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path=""
            element={<Navigate to="/portal/dashboard" replace />}
          />
        </Route>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}

export default App;
